import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { PreviewPanelsProvider as PlatformPreviewPanelsProvider } from '@atlassian/preview-panels-api';

const PreviewPanelsContext = createContext<{
	subscribe: (callback: (event: 'open' | 'close') => void) => () => void;
} | null>(null);

type EventType = 'open' | 'close';
type NotifyCallback = (event: EventType) => void;

export const PreviewPanelsProvider = ({ children }: { children: ReactNode }) => {
	const subscribers = useRef<Set<NotifyCallback>>(new Set());

	const notifySubscribers = useCallback((event: EventType) => {
		subscribers.current.forEach((callback) => callback(event));
	}, []);

	const handlePreviewPanelOpen = useCallback(() => {
		notifySubscribers('open');
	}, [notifySubscribers]);

	const handlePreviewPanelClose = useCallback(() => {
		notifySubscribers('close');
	}, [notifySubscribers]);

	const subscribe = useCallback((callback: NotifyCallback) => {
		subscribers.current.add(callback);

		return () => {
			subscribers.current.delete(callback);
		};
	}, []);

	const value = useMemo(
		() => ({
			subscribe,
		}),
		[subscribe],
	);

	return (
		<PreviewPanelsContext.Provider value={value}>
			<PlatformPreviewPanelsProvider
				onPreviewPanelOpen={handlePreviewPanelOpen}
				onPreviewPanelClose={handlePreviewPanelClose}
			>
				{children}
			</PlatformPreviewPanelsProvider>
		</PreviewPanelsContext.Provider>
	);
};

export const usePreviewPanelStateChanged = (callback: NotifyCallback) => {
	const context = useContext(PreviewPanelsContext);
	if (!context) {
		throw new Error('usePreviewPanelStateChanged must be used within a PreviewPanelsProvider');
	}

	const { subscribe } = context;

	useEffect(() => {
		const unsubscribe = subscribe(callback);
		return () => unsubscribe();
	}, [subscribe, callback]);
};
